import React from "react"

import { Button } from "@mantine/core"
import { useFormContext } from "react-hook-form"
import { notifications } from "@mantine/notifications"
import { Tooltip } from "@quorumus/plum"

import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/QuestionItem.module.css"
import { Icon } from "@/components"
import { CustomField } from "@/types/djangio"
import { EditQuestionModal } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/EditQuestionModal/EditQuestionModal"

type QuestionItemProps = {
    question: CustomField
    provided: Record<string, any>
    onDelete: () => void
}

export const QuestionItem = ({ question, onDelete, provided }: QuestionItemProps) => {
    const formContext = useFormContext()
    const personalizedPages = formContext.watch("personalized_campaign_pages")

    const [opened, setOpened] = React.useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(`{{${question.slug}}}`)
        notifications.show({
            id: "copied",
            title: "Copied",
            message: "Question placeholder copied to clipboard",
            autoClose: true,
            color: "green.0",
            icon: <Icon icon="check" color="green" />,
            closeButtonProps: { "aria-label": "Dismiss notification" },
        })
    }

    const handleFormUpdate = (question: CustomField) => {
        const questions = personalizedPages[0]
        const questionIndex = questions.findIndex((q) => q.id === question.id)
        if (questionIndex === -1) return
        personalizedPages[0][questionIndex] = question
        personalizedPages[0] = personalizedPages[0].filter((item) => {
            return !item.archived
        })

        formContext.setValue("personalized_campaign_pages", personalizedPages)
    }

    return (
        <div className={classes.questionItem} key={question.id} ref={provided.innerRef} {...provided.draggableProps}>
            <div className={classes.questionTitle}>
                <div className={classes.dragIcon} {...provided.dragHandleProps}>
                    <Icon icon="grip-vertical" iconFamily="fas" className={classes.dragIcon} />
                </div>
                <p className={classes.questionTitle}>{question.external_name}</p>
            </div>
            <div className={classes.questionButtons}>
                <Button
                    variant="subtle"
                    aria-label="Edit question"
                    className={classes.button}
                    onClick={() => setOpened(true)}
                >
                    <Icon icon="pen" />
                </Button>
                <Tooltip label="Copy question placeholder" position="top">
                    <Button
                        variant="subtle"
                        aria-label="Copy question placeholder"
                        onClick={handleCopy}
                        data-cy="copy-question-button"
                        className={classes.button}
                    >
                        <Icon icon="clone" />
                    </Button>
                </Tooltip>
                <Button
                    variant="subtle"
                    aria-label="Remove attached question"
                    onClick={onDelete}
                    className={classes.button}
                >
                    <Icon icon="times" />
                </Button>
            </div>

            {opened && (
                <EditQuestionModal
                    id={question.id}
                    onSuccessCallback={handleFormUpdate}
                    opened={opened}
                    onClose={() => setOpened(false)}
                />
            )}
        </div>
    )
}
